import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidV4 } from 'uuid';

import type {
    AssistantMessage,
    AssistantMessageFeedback,
    BaseChatMessage,
    ChatFlowCard,
    ChatMetadata,
    Conversation,
    ConversationSummary,
    FeedbackFormCardProps,
} from '@/src/shared/types';
import { CardType, ChatMessageRole } from '@/src/shared/types';

export const NEW_CONVERSATION_ID = 'new-conversation-id';

export interface ChatState {
    currentConversationId: string;
    conversationMap: {
        [conversationId: string]: Conversation | undefined;
    };
    chatMetadata: ChatMetadata;
    isChatting: boolean;
    loadingHistoryForConversationId: string;
    fillingFeedbackForActivityId: string;
    conversationSummaries: ConversationSummary[];
}

const createNewConversation = (): Conversation => ({
    conversationId: NEW_CONVERSATION_ID,
    messages: [],
});

function createFeedbackCards(feedback: AssistantMessageFeedback): ChatFlowCard[] {
    const cards: ChatFlowCard[] = [];
    if (!feedback.like) {
        const card: ChatFlowCard<FeedbackFormCardProps> = {
            activityId: uuidV4(),
            cardType: CardType.FeedbackFormCard,
            cardProps: {
                timestamp: feedback.feedbackTimestamp,
                feedback,
            },
        };
        cards.push(card);
    }
    cards.push({
        activityId: uuidV4(),
        cardType: CardType.FeedbackConfirmedCard,
        cardProps: {
            timestamp: feedback.feedbackTimestamp,
        },
    });
    return cards;
}

function formatMessages(messages: BaseChatMessage[]) {
    const assistantMessages = messages.filter(
        (m) => m.role === ChatMessageRole.ASSISTANT
    ) as AssistantMessage[];

    assistantMessages.forEach((m) => {
        const { additionalCards, feedback } = m;
        if (!additionalCards) {
            m.additionalCards = [];
        }
        if (feedback) {
            if (feedback.feedbackTimestamp) {
                m.additionalCards.push(...createFeedbackCards(feedback));
            } else {
                // when feedback is not available, set as undefined
                m.feedback = undefined;
            }
        }
    });
}

function findCurrentMessage(state: ChatState, activityId: string) {
    const currentConversation = state.conversationMap[state.currentConversationId];
    if (!currentConversation) {
        return undefined;
    }
    const currentMessage = currentConversation.messages.find((m) => m.activityId === activityId);
    return currentMessage;
}

export const initialState: ChatState = {
    currentConversationId: NEW_CONVERSATION_ID,
    conversationMap: {
        [NEW_CONVERSATION_ID]: createNewConversation(),
    },
    chatMetadata: {
        deep_think: false,
    },
    isChatting: false,
    loadingHistoryForConversationId: '',
    fillingFeedbackForActivityId: '',
    conversationSummaries: [],
};

export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        switchToNewConversation: (state) => {
            state.conversationMap[NEW_CONVERSATION_ID] = createNewConversation();
            state.currentConversationId = NEW_CONVERSATION_ID;
        },
        pushMessage: (
            state,
            { payload }: PayloadAction<{ conversationId: string; message: BaseChatMessage }>
        ) => {
            const { conversationId, message } = payload;

            // #region use real conversationId returned from server
            if (
                state.currentConversationId === NEW_CONVERSATION_ID &&
                conversationId !== NEW_CONVERSATION_ID
            ) {
                const currentConversation = state.conversationMap[
                    state.currentConversationId
                ] as Conversation;
                state.conversationMap[conversationId] = { ...currentConversation, conversationId };
                state.currentConversationId = conversationId;
                delete state.conversationMap[NEW_CONVERSATION_ID];

                // when new conversation id is created, we need to update the conversation summary list
                const queryMessage = state.conversationMap[conversationId].messages[0];
                state.conversationSummaries.unshift({
                    conversationId,
                    lastQuery: queryMessage.content,
                    lastQueryTimestamp: queryMessage.timestamp,
                });
            }
            // #endregion

            const conversation = state.conversationMap[conversationId];
            if (state.currentConversationId !== conversationId || !conversation) {
                return;
            }

            const { messages } = conversation;
            const existingMessageIndex = messages.findIndex(
                (m) => m.activityId === message.activityId
            );
            // add new message
            if (existingMessageIndex < 0) {
                messages.push(message);
                return;
            }

            // update existing message
            messages.splice(existingMessageIndex, 1, message);
        },
        setCurrentConversation: (state, { payload: conversationId }: PayloadAction<string>) => {
            if (!state.conversationMap[conversationId]) {
                return;
            }
            state.currentConversationId = conversationId;
        },
        appendLoadedConversationHistory: (
            state,
            { payload: conversation }: PayloadAction<Conversation>
        ) => {
            formatMessages(conversation.messages);

            state.conversationMap[conversation.conversationId] = conversation;
            state.loadingHistoryForConversationId = '';
            state.currentConversationId = conversation.conversationId;
        },
        removeConversations: (state, { payload }: PayloadAction<string[]>) => {
            payload.forEach((conversationId) => {
                delete state.conversationMap[conversationId];
            });
            const newConversationSummaries = state.conversationSummaries.filter(
                (conversationSummary) => !payload.includes(conversationSummary.conversationId)
            );
            state.conversationSummaries = newConversationSummaries;
        },
        updateChatMetadata: (state, { payload }: PayloadAction<Partial<ChatMetadata>>) => {
            state.chatMetadata = {
                ...state.chatMetadata,
                ...payload,
            };
        },
        setIsChatting(state, { payload }: PayloadAction<boolean>) {
            state.isChatting = payload;
        },
        setLoadingHistoryForConversationId(
            state,
            { payload: conversationId }: PayloadAction<string>
        ) {
            if (state.conversationMap[conversationId]) {
                state.currentConversationId = conversationId;
                return;
            }

            state.loadingHistoryForConversationId = conversationId;
        },
        addCardForMessage(
            state,
            { payload }: PayloadAction<{ activityId: string; card: ChatFlowCard }>
        ) {
            const { activityId, card } = payload;
            state.fillingFeedbackForActivityId = activityId;
            // add temp card
            const currentMessage = findCurrentMessage(state, activityId) as AssistantMessage;
            currentMessage.additionalCards.push(card);
        },
        removeCardForMessage(
            state,
            { payload }: PayloadAction<{ activityId: string; cardActivityId: string }>
        ) {
            const { activityId, cardActivityId } = payload;
            state.fillingFeedbackForActivityId = '';

            const currentMessage = findCurrentMessage(state, activityId) as AssistantMessage;
            currentMessage.additionalCards = currentMessage.additionalCards.filter(
                (card) => card.activityId !== cardActivityId
            );
        },
        updateFeedbackForMessage(
            state,
            { payload }: PayloadAction<{ activityId: string; feedback: AssistantMessageFeedback }>
        ) {
            const { activityId, feedback } = payload;
            const currentMessage = findCurrentMessage(state, activityId) as AssistantMessage;
            currentMessage.feedback = feedback;
            formatMessages([currentMessage]);
        },
        setConversationSummaries(state, { payload }: PayloadAction<ConversationSummary[]>) {
            state.conversationSummaries = payload;
        },
        appendConversationSummaries(state, { payload }: PayloadAction<ConversationSummary[]>) {
            state.conversationSummaries = state.conversationSummaries.concat(payload);
        },
    },
});

export const chatActions = chatSlice.actions;
